import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';

import { CustomReportService } from '../custom-report-editor/custom-report.service';

interface ScheduleSlotLike {
    id?: string;
    frequency?: number;
    mode?: number;
    startTime?: string | null;
    endTime?: string | null;
    startDate?: string | null;
    dayOfWeek?: number | null;
    dayOfMonth?: number | null;
}

interface ScheduleSlotPresetLike {
    id: string;
    name: string;
    description?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    slots?: ScheduleSlotLike[];
}

export interface ScheduleSlotPresetEditorDialogData {
    presetId?: string | null;
    targetType?: number | null;
    allowedModes?: number[] | null;
}

@UntilDestroy()
@Component({
    selector: 'app-schedule-slot-preset-editor',
    templateUrl: './schedule-slot-preset-editor.component.html',
    styleUrls: ['./schedule-slot-preset-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ScheduleSlotPresetEditorComponent implements OnInit {

    presetForm: FormGroup;
    isSaving = false;
    isDeleting = false;
    deleteErrorMessage = '';
    editingPresetId: string | null = null;

    readonly frequencyOptions = [
        { value: 6, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.FrequencyHourly' },
        { value: 1, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.FrequencyDaily' },
        { value: 2, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.FrequencyWeekly' },
        { value: 3, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.FrequencyMonthly' },
        { value: 4, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.FrequencyYearly' }
    ];

    readonly dayOfWeekOptions = [
        { value: 1, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.DayMonday' },
        { value: 2, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.DayTuesday' },
        { value: 3, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.DayWednesday' },
        { value: 4, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.DayThursday' },
        { value: 5, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.DayFriday' },
        { value: 6, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.DaySaturday' },
        { value: 0, labelKey: 'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.DaySunday' }
    ];

    readonly monthOptions = Array.from({ length: 12 }, (_, index) => ({
        value: index + 1,
        label: String(index + 1)
    }));

    readonly minuteOptions = Array.from({ length: 60 }, (_, index) => index.toString().padStart(2, '0'));

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ScheduleSlotPresetEditorDialogData,
        private dialogRef: MatDialogRef<ScheduleSlotPresetEditorComponent>,
        private formBuilder: FormBuilder,
        private customReportService: CustomReportService,
        private translate: TranslateService
    ) {
        this.presetForm = this.formBuilder.group({
            name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(64)]],
            description: [''],
            startDate: [null],
            endDate: [null],
            slots: this.formBuilder.array([])
        });
    }

    get slotArray(): FormArray {
        return this.presetForm.get('slots') as FormArray;
    }

    get isEditMode(): boolean {
        return !!this.editingPresetId;
    }

    get isSingleSlotMode(): boolean {
        return this.getSupportedModes().length === 1;
    }

    ngOnInit(): void {
        this.resetForm();

        if (this.data?.presetId) {
            this.editingPresetId = this.data.presetId;
            this.loadPreset(this.data.presetId);
        }
    }

    addSlot(): void {
        this.slotArray.push(this.createSlotGroup());
    }

    removeSlot(index: number): void {
        if (this.slotArray.length > 1) {
            this.slotArray.removeAt(index);
        }
    }

    onFrequencyChange(index: number): void {
        this.applyFrequencyState(this.slotArray.at(index) as FormGroup);
    }

    onModeChange(index: number): void {
        this.applyModeState(this.slotArray.at(index) as FormGroup);
    }

    save(): void {
        if (this.isDeleting || this.presetForm.invalid) {
            this.presetForm.markAllAsTouched();
            return;
        }

        this.isSaving = true;
        this.deleteErrorMessage = '';

        const rawSlots = this.slotArray.getRawValue();
        const payload = {
            name: this.presetForm.get('name')?.value,
            description: this.presetForm.get('description')?.value,
            startDate: this.presetForm.get('startDate')?.value || null,
            endDate: this.presetForm.get('endDate')?.value || null,
            slots: rawSlots.map((slot: any) => ({
                frequency: Number(slot.frequency),
                mode: this.resolveSupportedMode(Number(slot.mode)),
                dayOfWeek: slot.dayOfWeek !== null && slot.dayOfWeek !== undefined ? Number(slot.dayOfWeek) : null,
                dayOfMonth: slot.dayOfMonth !== null && slot.dayOfMonth !== undefined ? Number(slot.dayOfMonth) : null,
                startDate: this.buildStartDateForSlot(slot),
                startTime: Number(slot.frequency) === 6 ? this.normalizeHourlyTime(slot.startTime) : (slot.startTime || null),
                endTime: this.resolveSupportedMode(Number(slot.mode)) === 2
                    ? (Number(slot.frequency) === 6 ? this.normalizeHourlyTime(slot.endTime) : (slot.endTime || null))
                    : null,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }))
        };

        const request$ = this.editingPresetId
            ? this.customReportService.updateScheduleSlotPreset(this.editingPresetId, payload)
            : this.customReportService.createScheduleSlotPreset(payload);

        request$.pipe(untilDestroyed(this)).subscribe({
            next: (preset) => {
                this.isSaving = false;
                this.dialogRef.close(preset);
            },
            error: () => {
                this.isSaving = false;
            }
        });
    }

    deletePreset(): void {
        if (!this.editingPresetId || this.isSaving || this.isDeleting) {
            return;
        }

        const confirmed = window.confirm(
            this.translate.instant('SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.ConfirmDeletePreset')
        );

        if (!confirmed) {
            return;
        }

        const presetId = this.editingPresetId;
        this.isDeleting = true;
        this.deleteErrorMessage = '';

        this.customReportService.deleteScheduleSlotPreset(presetId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.isDeleting = false;
                    this.dialogRef.close({ deleted: true, id: presetId });
                },
                error: () => {
                    this.isDeleting = false;
                    this.deleteErrorMessage = this.translate.instant(
                        'SystemAdmin.Components.CustomReportEditor.Tab.Schedule.Form.DeletePresetError'
                    );
                }
            });
    }

    close(): void {
        if (this.isDeleting) {
            return;
        }

        this.dialogRef.close();
    }

    isSlotModeSupported(mode: number): boolean {
        return this.getSupportedModes().includes(mode);
    }

    private loadPreset(presetId: string): void {
        this.customReportService.getScheduleSlotPresets({
            targetType: this.data?.targetType ?? null
        }).pipe(untilDestroyed(this)).subscribe({
            next: (presets) => {
                const preset = (presets ?? []).find(item => item?.id === presetId);
                if (!preset) {
                    return;
                }

                this.fillForm(preset as ScheduleSlotPresetLike);
            }
        });
    }

    private resetForm(): void {
        const defaultDateRange = this.createDefaultDateRange();

        this.presetForm.reset({
            name: '',
            description: '',
            startDate: defaultDateRange.startDate,
            endDate: defaultDateRange.endDate
        }, { emitEvent: false });

        while (this.slotArray.length > 0) {
            this.slotArray.removeAt(0);
        }

        this.slotArray.push(this.createSlotGroup());
    }

    private fillForm(preset: ScheduleSlotPresetLike): void {
        this.presetForm.patchValue({
            name: preset?.name || '',
            description: preset?.description || '',
            startDate: preset?.startDate ? this.normalizeDateInput(preset.startDate) : null,
            endDate: preset?.endDate ? this.normalizeDateInput(preset.endDate) : null
        }, { emitEvent: false });

        while (this.slotArray.length > 0) {
            this.slotArray.removeAt(0);
        }

        const slots = Array.isArray(preset?.slots) ? preset.slots : [];
        if (slots.length === 0) {
            this.slotArray.push(this.createSlotGroup());
            return;
        }

        slots.forEach(slot => this.slotArray.push(this.createSlotGroup(slot)));
    }

    private createSlotGroup(slot?: ScheduleSlotLike): FormGroup {
        const defaultMode = this.getSupportedModes()[0];
        const group = this.formBuilder.group({
            mode: [defaultMode, Validators.required],
            frequency: [2, Validators.required],
            dayOfWeek: [1],
            dayOfMonth: [1],
            monthOfYear: [1],
            startDate: [null],
            startTime: ['09:00'],
            endTime: ['18:00']
        });

        if (slot) {
            group.patchValue({
                mode: this.resolveSupportedMode(Number(slot.mode ?? defaultMode)),
                frequency: slot.frequency ?? 2,
                dayOfWeek: slot.dayOfWeek ?? null,
                dayOfMonth: slot.dayOfMonth ?? null,
                monthOfYear: this.resolveMonthOfYear(slot),
                startDate: slot.startDate ? this.normalizeDateInput(slot.startDate) : null,
                startTime: this.normalizeTimeInput(slot.startTime) ?? '09:00',
                endTime: this.normalizeTimeInput(slot.endTime) ?? '18:00'
            }, { emitEvent: false });
        }

        group.get('frequency')?.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(value => this.applyFrequencyState(group, Number(value)));

        this.applyFrequencyState(group, Number(group.get('frequency')?.value ?? 2));
        this.applyModeState(group);

        return group;
    }

    private applyFrequencyState(group: FormGroup, frequency?: number): void {
        const value = Number(frequency ?? group.get('frequency')?.value ?? 2);
        const dayOfWeekCtrl = group.get('dayOfWeek');
        const dayOfMonthCtrl = group.get('dayOfMonth');
        const monthOfYearCtrl = group.get('monthOfYear');
        const slotStartDateCtrl = group.get('startDate');
        const startTimeCtrl = group.get('startTime');
        const endTimeCtrl = group.get('endTime');

        if (!dayOfWeekCtrl || !dayOfMonthCtrl || !monthOfYearCtrl) {
            return;
        }

        if (value === 2) {
            dayOfWeekCtrl.enable({ emitEvent: false });
            if (dayOfWeekCtrl.value === null || dayOfWeekCtrl.value === undefined) {
                dayOfWeekCtrl.setValue(1, { emitEvent: false });
            }
            dayOfMonthCtrl.disable({ emitEvent: false });
            dayOfMonthCtrl.setValue(null, { emitEvent: false });
            monthOfYearCtrl.disable({ emitEvent: false });
            monthOfYearCtrl.setValue(null, { emitEvent: false });
            slotStartDateCtrl?.disable({ emitEvent: false });
            slotStartDateCtrl?.setValue(null, { emitEvent: false });
            return;
        }

        if (value === 3) {
            dayOfWeekCtrl.disable({ emitEvent: false });
            dayOfWeekCtrl.setValue(null, { emitEvent: false });
            dayOfMonthCtrl.enable({ emitEvent: false });
            if (dayOfMonthCtrl.value === null || dayOfMonthCtrl.value === undefined) {
                dayOfMonthCtrl.setValue(1, { emitEvent: false });
            }
            monthOfYearCtrl.disable({ emitEvent: false });
            monthOfYearCtrl.setValue(null, { emitEvent: false });
            slotStartDateCtrl?.disable({ emitEvent: false });
            slotStartDateCtrl?.setValue(null, { emitEvent: false });
            return;
        }

        if (value === 4) {
            dayOfWeekCtrl.disable({ emitEvent: false });
            dayOfWeekCtrl.setValue(null, { emitEvent: false });
            dayOfMonthCtrl.enable({ emitEvent: false });
            if (dayOfMonthCtrl.value === null || dayOfMonthCtrl.value === undefined) {
                dayOfMonthCtrl.setValue(1, { emitEvent: false });
            }
            monthOfYearCtrl.enable({ emitEvent: false });
            if (monthOfYearCtrl.value === null || monthOfYearCtrl.value === undefined) {
                monthOfYearCtrl.setValue(1, { emitEvent: false });
            }
            slotStartDateCtrl?.disable({ emitEvent: false });
            slotStartDateCtrl?.setValue(null, { emitEvent: false });
            return;
        }

        if (value === 6) {
            dayOfWeekCtrl.disable({ emitEvent: false });
            dayOfWeekCtrl.setValue(null, { emitEvent: false });
            dayOfMonthCtrl.disable({ emitEvent: false });
            dayOfMonthCtrl.setValue(null, { emitEvent: false });
            monthOfYearCtrl.disable({ emitEvent: false });
            monthOfYearCtrl.setValue(null, { emitEvent: false });
            slotStartDateCtrl?.disable({ emitEvent: false });
            slotStartDateCtrl?.setValue(null, { emitEvent: false });

            if (startTimeCtrl?.value) {
                startTimeCtrl.setValue(this.normalizeHourlyTime(startTimeCtrl.value), { emitEvent: false });
            }
            if (endTimeCtrl?.value) {
                endTimeCtrl.setValue(this.normalizeHourlyTime(endTimeCtrl.value), { emitEvent: false });
            }
            return;
        }

        dayOfWeekCtrl.disable({ emitEvent: false });
        dayOfWeekCtrl.setValue(null, { emitEvent: false });
        dayOfMonthCtrl.disable({ emitEvent: false });
        dayOfMonthCtrl.setValue(null, { emitEvent: false });
        monthOfYearCtrl.disable({ emitEvent: false });
        monthOfYearCtrl.setValue(null, { emitEvent: false });
        slotStartDateCtrl?.disable({ emitEvent: false });
        slotStartDateCtrl?.setValue(null, { emitEvent: false });
    }

    private applyModeState(group: FormGroup): void {
        const mode = this.resolveSupportedMode(Number(group.get('mode')?.value));
        const endTimeControl = group.get('endTime');

        if (Number(group.get('mode')?.value) !== mode) {
            group.get('mode')?.setValue(mode, { emitEvent: false });
        }

        if (mode === 2) {
            endTimeControl?.enable({ emitEvent: false });
        } else {
            endTimeControl?.disable({ emitEvent: false });
            endTimeControl?.setValue(null, { emitEvent: false });
        }
    }

    private buildStartDateForSlot(slot: any): string | null {
        if (Number(slot.frequency) === 4) {
            const month = Number(slot.monthOfYear || 1);
            const day = Number(slot.dayOfMonth || 1);
            const year = new Date().getFullYear();
            const date = new Date(Date.UTC(year, month - 1, day));
            return date.toISOString().slice(0, 10);
        }

        if (Number(slot.frequency) === 3) {
            return null;
        }

        return slot.startDate || null;
    }

    private resolveMonthOfYear(slot: ScheduleSlotLike): number | null {
        if (!slot?.startDate) {
            return null;
        }

        if (typeof slot.startDate === 'string' && slot.startDate.length >= 10) {
            const parts = slot.startDate.slice(0, 10).split('-');
            const month = Number(parts[1]);
            return Number.isFinite(month) ? month : null;
        }

        const date = new Date(slot.startDate);
        if (!Number.isNaN(date.getTime())) {
            return date.getMonth() + 1;
        }

        return null;
    }

    private normalizeDateInput(value: string | Date | null | undefined): string | null {
        if (!value) {
            return null;
        }

        if (typeof value === 'string') {
            return value.length >= 10 ? value.slice(0, 10) : value;
        }

        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return value.toISOString().slice(0, 10);
        }

        return null;
    }

    private normalizeTimeInput(value?: string | null): string | null {
        if (!value) {
            return null;
        }

        const parts = value.split(':');
        if (parts.length < 2) {
            return value;
        }

        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }

    private normalizeHourlyTime(value?: string | null): string | null {
        if (!value) {
            return value ?? null;
        }

        const parts = value.split(':');
        if (parts.length < 2) {
            return value;
        }

        const minutes = parts[1].padStart(2, '0');
        if (parts.length > 2) {
            const seconds = parts[2].padStart(2, '0');
            return `00:${minutes}:${seconds}`;
        }

        return `00:${minutes}`;
    }

    private getSupportedModes(): number[] {
        const modes = Array.isArray(this.data?.allowedModes)
            ? this.data.allowedModes.filter(mode => mode === 1 || mode === 2)
            : [];
        const unique = Array.from(new Set(modes));
        return unique.length > 0 ? unique : [1, 2];
    }

    private resolveSupportedMode(mode: number): number {
        const supportedModes = this.getSupportedModes();
        return supportedModes.includes(mode) ? mode : supportedModes[0];
    }

    private createDefaultDateRange(): { startDate: string; endDate: string } {
        const startDate = new Date();
        const endDate = new Date(startDate);
        endDate.setFullYear(endDate.getFullYear() + 1);

        return {
            startDate: this.toLocalDateInputValue(startDate),
            endDate: this.toLocalDateInputValue(endDate)
        };
    }

    private toLocalDateInputValue(value: Date): string {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
